import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { io } from "socket.io-client";
import { MapsWidget } from "../../Widgets/MapsWidget";
import { OperationStatusWidget } from "../../Widgets/OperationStatusWidget";
import { websocketPort } from '../../../server/websocketPort';
import useVideowallMode from '../../../utils/hooks/useVideowallMode';

const VideoWallWrapper = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000000;
  overflow: hidden;
`;

const VideoWallContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 4px;
  width: 7680px;
  height: 1080px;
  background-color: #000000;
  overflow: hidden;
  padding: 4px;
  transform-origin: center center;
  transform: scale(${props => props.$scaleX || 1}, ${props => props.$scaleY || 1});
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
`;

const Monitor = styled.div`
  position: relative;
  background-color: ${props => props.$enabled ? '#1a1a1a' : '#000000'};
  border: ${props => props.$enabled ? '3px solid #86BC25' : '3px solid #222'};
  display: ${props => props.$hidden ? 'none' : 'flex'};
  /* 4×2 grid: zone 1-4 in row 1, zone 5-8 in row 2 */
  /* Span across 2 columns for grouped zones */
  grid-column: ${props => {
    const zoneId = props.$zoneId;
    if (props.$span) {
      // Spanning zones (map: 2-3, operations: 6-7)
      if (zoneId === 2) return '2 / 4'; // zones 2-3
      if (zoneId === 6) return '2 / 4'; // zones 6-7
    }
    return zoneId <= 4 ? zoneId : zoneId - 4;
  }};
  grid-row: ${props => props.$zoneId <= 4 ? 1 : 2};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  overflow: hidden;
  width: 100%;
  height: 100%;
`;

const MonitorLabel = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  font-size: 14px;
  font-weight: bold;
  color: ${props => props.$enabled ? '#86BC25' : '#444'};
  background-color: rgba(0, 0, 0, 0.7);
  padding: 5px 10px;
  border-radius: 4px;
  z-index: 10;
`;

const MonitorContent = styled.div`
  width: 100%;
  height: 100%;
  display: ${props => props.$enabled ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const VideoWallDisplay = () => {
  const [zones, setZones] = useState(
    Array.from({ length: 8 }, (_, i) => ({
      id: i + 1,
      enabled: true,
      row: Math.floor(i / 4),
      col: i % 4
    }))
  );
  const [testMode, setTestMode] = useState(false);
  const [scaleX, setScaleX] = useState(1);
  const [scaleY, setScaleY] = useState(1);
  const [simulatedWidth, setSimulatedWidth] = useState(7680);
  const [simulatedHeight, setSimulatedHeight] = useState(1080);
  const wrapperRef = React.useRef(null);

  // Inject --vw CSS variable + videowall class so theme vw-values and
  // component media queries resolve against the 7680px design canvas.
  useVideowallMode(7680, 1080);

  // Calculate scale based on test mode or actual viewport
  useEffect(() => {
    const calculateScale = () => {
      if (testMode) {
        // In test mode, use simulated dimensions
        const newScaleX = simulatedWidth / 7680;
        const newScaleY = simulatedHeight / 1080;
        setScaleX(newScaleX);
        setScaleY(newScaleY);
      } else {
        // In normal mode, use actual body/viewport dimensions
        const availableWidth = document.body.clientWidth;
        const availableHeight = document.body.clientHeight;
        
        const newScaleX = availableWidth / 7680;
        const newScaleY = availableHeight / 1080;
        
        setScaleX(newScaleX);
        setScaleY(newScaleY);
      }
    };

    // Delay calculation to ensure DOM is ready
    setTimeout(calculateScale, 0);
    window.addEventListener('resize', calculateScale);
    
    return () => window.removeEventListener('resize', calculateScale);
  }, [testMode, simulatedWidth, simulatedHeight]);

  useEffect(() => {
    const socket = io(websocketPort, {
      transports: ['websocket'],
      reconnection: true
    });

    socket.on('connect', () => {
      console.log('VideoWallDisplay: Connected to WebSocket:', websocketPort);
    });

    socket.on('videowall_zone_update', (data) => {
      console.log('VideoWallDisplay: Received zone update:', data);
      if (data.zones) {
        setZones(data.zones);
      }
      if (data.testSettings) {
        setTestMode(data.testSettings.testMode);
        if (data.testSettings.testMode) {
          setSimulatedWidth(data.testSettings.width);
          setSimulatedHeight(data.testSettings.height);
        }
      }
    });

    socket.on('disconnect', () => {
      console.log('VideoWallDisplay: Disconnected from WebSocket');
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  return (
    <VideoWallWrapper>
      {testMode && (
        <div style={{ 
          position: 'absolute',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '8px 16px', 
          backgroundColor: '#FF6B35', 
          color: 'white', 
          fontSize: '14px',
          fontWeight: 'bold',
          borderRadius: '4px',
          zIndex: 9999
        }}>
          TEST MODE: {simulatedWidth}x{simulatedHeight} | Scale: {scaleX.toFixed(3)}x, {scaleY.toFixed(3)}y
        </div>
      )}
      <VideoWallContainer $scaleX={scaleX} $scaleY={scaleY}>
      {zones.map((zone) => {
        // Hide zone 3 and 7 (they're part of the span from zones 2 and 6)
        const isHidden = zone.id === 3 || zone.id === 7;
        const isMapZone = zone.id === 2;
        const isOperationZone = zone.id === 6;
        const shouldSpan = isMapZone || isOperationZone;
        
        return (
          <Monitor 
            key={zone.id} 
            $enabled={zone.enabled}
            $zoneId={zone.id}
            $hidden={isHidden}
            $span={shouldSpan}
          >
            <MonitorLabel $enabled={zone.enabled}>
              {isMapZone ? 'Map Widget' : isOperationZone ? 'Operation Status' : `Zone ${zone.id}`}
            </MonitorLabel>
            <MonitorContent $enabled={zone.enabled}>
              {isMapZone ? (
                <div style={{ width: '100%', height: '100%' }}>
                  <MapsWidget />
                </div>
              ) : isOperationZone ? (
                <div style={{ width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
                  <OperationStatusWidget />
                </div>
              ) : null}
            </MonitorContent>
          </Monitor>
        );
      })}
      </VideoWallContainer>
    </VideoWallWrapper>
  );
};

export default VideoWallDisplay;
